import React, { useEffect } from 'react';
import { MdDevices } from "react-icons/md";
import { formatDeviceInfo, getOsIcon, getBrowserIcon } from "../../utils/deviceIcons";
import ActionDropdown from "../ActionDropdown";
import { showConfirm, showSuccess, showError } from "../../utils/dialog";
import { formatAppDateTime } from "../../utils/appDateTime";

// Map OS/browser names to local image assets when available — otherwise return null so we can fallback to react-icons
const mapOsToIcon = (osName) => {
  if (!osName) return null;
  const s = osName.toString().toLowerCase();
  if (s.includes('windows')) return '/windows-os.png';
  if (s.includes('mac') || s.includes('os x') || s.includes('darwin')) return '/mac-monitor.png';
  if (s.includes('chrome os')) return '/window.png';
  // no android asset in public/, fallback to icon component
  if (s.includes('linux')) return null;
  return null;
};

const mapBrowserToIcon = (browser) => {
  if (!browser) return null;
  const s = browser.toString().toLowerCase();
  if (s.includes('chrome')) return '/chrome.png';
  if (s.includes('safari')) return '/safari.png';
  // firefox and edge images missing in public; fallback to icon component
  return null;
};

const countryToEmoji = (country) => {
  if (!country) return '';
  try {
    const code = (country.length === 2 ? country : country.slice(0,2)).toUpperCase();
    return code.split('').map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('');
  } catch (e) {
    return '';
  }
};

const TrustedDevicesForm = ({ onClose, devices = [], onRemoveDevice }) => {
  const handleRowRemove = async (id) => {
    if (!onRemoveDevice) return;
    // Delegate confirmation to parent handler so UX is consistent
    try {
      await onRemoveDevice(id);
    } catch (err) {
      console.error('Remove device error', err);
      showError('Failed to remove device');
    }
  };

  const handleRemoveAll = async () => {
    if (!devices || devices.length === 0) return;
    if (!(await showConfirm(`Remove all ${devices.length} trusted devices?`))) return;
    for (const d of devices) {
      try {
        // pass skipConfirm = true to avoid repeated prompts
        await onRemoveDevice(d.trusted_device_id, true);
      } catch (err) {
        console.warn('Failed to remove device', d.trusted_device_id, err);
      }
    }
    showSuccess('Requested removal of all devices');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-xl p-6 relative max-h-[90vh] sm:max-h-[80vh]">
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
        >
          ✖
        </button>

        {/* Header */}
        <div className="flex">
          <MdDevices className="text-buttonRed text-lg w-6 h-6 mt-1 mr-2"/>
          <h2 className="text-xl text-formTitleText font-semibold mb-4">Trusted Devices</h2>
        </div>
        
        <div className="h-[1px] bg-[#DC1F004D]"></div>

        {/* Form */}
        <form 
          onSubmit={(e) => e.preventDefault()}
          className="flex-1 overflow-y-auto max-h-[70vh] sm:max-h-[60vh] pb-20"
        >
          <div className="w-full mt-4 mb-4">
            {/* Desktop Header */}
            <div className="hidden sm:flex items-center pb-2 border-b border-gray-200 text-left font-medium text-gray-600">
              <div className="w-[30%] pl-2">Device & Browser</div>
              <div className="w-[25%]">Location</div>
              <div className="w-[20%]">Last Sign In</div>
              <div className="w-[20%]">Added</div>
              <div className="w-[5%]"></div>
            </div>

            <div className="flex flex-col gap-4 sm:gap-0 mt-4 sm:mt-0">
              {devices.length === 0 && (
                <div className="text-center text-gray-500 py-8">No trusted devices found</div>
              )}

              {devices.map(device => {
                // raw device log to help debug Android/Linux misclassification
                console.log('🔁 TrustedDevicesForm device object:', device);
                const info = formatDeviceInfo(device);
                const last = device.signin_date || device.activity_date || device.signinDate || device.activityDate || null;
                const added = device.create_date || device.createDate || device.created_at || device.createDateTime || null;
                const osIcon = mapOsToIcon(device.os_name || device.os_type || info.osName);
                const browserIcon = mapBrowserToIcon(device.browser_type || info.browserName);
                const countryEmoji = countryToEmoji((device.country || '').toString().slice(0,2));
                return (
                  <div key={device.trusted_device_id} className="flex flex-col sm:flex-row sm:items-center justify-between border sm:border-0 sm:border-b border-gray-200 rounded-lg sm:rounded-none p-4 sm:p-2 sm:py-4 gap-3 sm:gap-0">
                    
                    {/* Device & Browser */}
                    <div className="flex items-center gap-3 sm:w-[30%] sm:pl-2">
                      <div className="flex items-center justify-center shrink-0">
                        {osIcon ? (
                          <img src={osIcon} alt="os" className="w-6 h-6" />
                        ) : (
                          <span>{getOsIcon(info.osName || device.os_name || device.os_type, 22)}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {browserIcon ? (
                          <img src={browserIcon} alt="browser" className="w-5 h-5" />
                        ) : (
                          <span>{getBrowserIcon(info.browserName || device.browser_type, 18)}</span>
                        )}
                        <span className="text-sm font-medium sm:font-normal text-gray-800 sm:text-gray-700">{info.browserName || device.browser_type || 'Unknown Browser'}</span>
                      </div>
                    </div>
                    
                    {/* Location */}
                    <div className="flex flex-col sm:w-[25%] mt-2 sm:mt-0">
                      <span className="text-xs sm:hidden text-gray-500 mb-0.5">Location</span>
                      <span className="text-sm font-medium text-gray-700 leading-tight">
                        {[device.city, device.region].filter(Boolean).join(', ') || 'Unknown Location'}
                      </span>
                      <span className="text-xs text-textGray mt-0.5">
                        {countryEmoji} {device.country || 'Unknown Country'}
                      </span>
                      <span className="text-xs text-gray-400 mt-0.5">
                        {info.ip || device.ip || ''}
                      </span>
                    </div>

                    {/* Last Sign In */}
                    <div className="flex flex-col sm:w-[20%] mt-2 sm:mt-0">
                      <span className="text-xs sm:hidden text-gray-500 mb-0.5">Last Sign In</span>
                      <span className="text-sm text-gray-700">{formatAppDateTime(last, { emptyValue: 'Unknown' })}</span>
                    </div>

                    {/* Added */}
                    <div className="flex flex-col sm:w-[20%] mt-2 sm:mt-0">
                      <span className="text-xs sm:hidden text-gray-500 mb-0.5">Added</span>
                      <span className="text-sm text-gray-700">{formatAppDateTime(added, { emptyValue: 'Unknown' })}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end sm:justify-end sm:w-[5%] mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-0 border-gray-100">
                      <ActionDropdown
                        actions={[
                          { label: 'Sign Out', onClick: () => handleRowRemove(device.trusted_device_id) },
                          { label: 'Delete', onClick: () => handleRowRemove(device.trusted_device_id), danger: true }
                        ]}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </form>

        {/* Sticky footer with actions — stays visible while list scrolls */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t px-4 sm:px-6 py-3 rounded-b-xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <button
              type="button"
              onClick={handleRemoveAll}
              className="w-full border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
            >
              Sign Out All
            </button>
            <button
              type="button"
              onClick={handleRemoveAll}
              className="w-full bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
            >
              Delete All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustedDevicesForm;